import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Download, LinkIcon, Sheet, Share, WhatsApp, useToast } from '@/components/ui';
import { ShareLinkSheet } from '@/features/care/ShareLinkSheet';
import { useT } from '@/i18n';
import { formatDate } from '@/lib/format';
import { api } from '@/api/client';
import { offersWhatsApp } from '@/features/auth/channels';
import { useSession } from '@/hooks/session';
import type { ReportDetail, Tier } from '@/api/types';


export interface ReportActionsProps {
  report: ReportDetail['report'];
  tier: Tier;
  /** Sibling top-level field on `ReportDetail`, not on `report` — passed separately so
   * `report.update` / `report.updatesLeft` can read remaining regenerations and their expiry. */
  entitlement?: ReportDetail['entitlement'];
  className?: string;
}

/**
 * Download / share / resend / update actions for a report. `report.download` works at any tier
 * (it fetches whichever tier's PDF is passed in); `report.share` and `report.resend` only make
 * sense once the report is unlocked; `report.update` additionally needs at least one
 * regeneration left on the entitlement.
 */
export const ReportActions = ({ report, tier, entitlement, className }: ReportActionsProps) => {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { account, config } = useSession();
  // A sign-in link on WhatsApp needs WhatsApp live and a verified number (spec WA3).
  const canResendOnWhatsApp = offersWhatsApp(config) && Boolean(account?.phoneVerified);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [resending, setResending] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  const [linkSheetOpen, setLinkSheetOpen] = useState(false);
  const hasPdf = report.status === 'unlocked' || report.status === 'preview_available';

  const unlocked = report.status === 'unlocked';
  const canUpdate = unlocked && (entitlement?.regenerationsRemaining ?? 0) > 0;

  // The focus question is step one of checkout now, before payment, so by the
  // time a plan can be downloaded it has already been asked.
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { url } = await api.reports.download(report.reportId, tier);
      window.open(url, '_blank', 'noopener');
    } finally {
      setDownloading(false);
    }
  };

  const canUseWebShare = () =>
    typeof navigator !== 'undefined' &&
    typeof navigator.canShare === 'function' &&
    typeof navigator.share === 'function';

  const handleShare = async () => {
    if (!canUseWebShare()) {
      setShareSheetOpen(true);
      return;
    }
    setSharing(true);
    try {
      const { url, filename } = await api.reports.download(report.reportId, tier);
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], filename, { type: 'application/pdf' });
      if (navigator.canShare!({ files: [file] })) {
        await navigator.share!({ files: [file], title: t('app.name') });
        return;
      }
    } catch {
      // Fetch, File construction or navigator.share can all reject (offline, user cancelled the
      // share sheet, unsupported file type) — the safety sheet is always a valid fallback.
    } finally {
      setSharing(false);
    }
    setShareSheetOpen(true);
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await api.reports.deliver(report.reportId, ['whatsapp']);
      toast(t('report.resent'));
    } catch {
      toast(t('report.resendFailed'));
    } finally {
      setResending(false);
    }
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const { assessmentId } = await api.reports.reopen(report.reportId);
      navigate(`/assessments/${assessmentId}`);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="secondary" icon={<Download className="h-4 w-4" />} loading={downloading} onClick={() => void handleDownload()}>
          {t('report.download')}
        </Button>

        {unlocked && (
          <Button icon={<Share className="h-4 w-4" />} loading={sharing} onClick={() => void handleShare()}>
            {t('report.share')}
          </Button>
        )}

        {unlocked && canResendOnWhatsApp && (
          <Button variant="secondary" icon={<WhatsApp className="h-4 w-4" />} loading={resending} onClick={() => void handleResend()}>
            {t('report.resend')}
          </Button>
        )}

        {hasPdf && (
          <Button variant="secondary" icon={<LinkIcon className="h-4 w-4" />} onClick={() => setLinkSheetOpen(true)}>
            {t('report.link')}
          </Button>
        )}

        {canUpdate && (
          <Button variant="tertiary" loading={updating} onClick={() => void handleUpdate()}>
            {t('report.update')}
          </Button>
        )}
      </div>

      {canUpdate && entitlement && (
        <p className="mt-2 text-caption text-ink-muted">
          {t('report.updatesLeft', { n: entitlement.regenerationsRemaining, date: formatDate(entitlement.expiresAt) })}
        </p>
      )}

      {hasPdf && (
        <ShareLinkSheet
          open={linkSheetOpen}
          onClose={() => setLinkSheetOpen(false)}
          reportId={report.reportId}
          childId={report.childId}
          childName={report.childName}
        />
      )}

      <Sheet open={shareSheetOpen} onClose={() => setShareSheetOpen(false)} title={t('report.share')}>
        <p className="text-body text-ink-soft">{t('report.shareSafety')}</p>
        <div className="mt-4 space-y-3">
          <div>
            <p className="text-label text-ink">{t('report.shareStep1')}</p>
            <Button className="mt-2" icon={<Download className="h-4 w-4" />} loading={downloading} onClick={() => void handleDownload()}>
              {t('report.download')}
            </Button>
          </div>
          <p className="text-label text-ink">{t('report.shareStep2')}</p>
        </div>
      </Sheet>
    </div>
  );
};
